import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { GuestApi } from '../api/client';
import { asFailure } from '../api/errors';
import type { ApiFailure } from '../api/errors';
import type { SiteResponse } from '../wire';
import { applyTheme, resolveTheme } from '../theme/theme';
import type { ResolvedTheme } from '../theme/theme';
import { arrangeSections } from '../sections/registry';
import { SectionView } from '../sections/blocks';
import { SiteProvider, useSite } from './context';
import { BookingFlow } from './Booking';
import { Visits } from './Visits';
import { Busy, Notice } from './bits';

/**
 * The shell.
 *
 * **One static build, every tenant hostname** (spec §6.4.2). The bundle knows
 * nothing about who it is until `GET /api/site` answers from the Host header,
 * so booting is: ask, resolve the theme from the branding row, arrange the
 * sections from the same row, paint. A rebrand is a row update; a new tenant is
 * three rows and zero deployments.
 *
 * **No router.** `apps/app` has no router because it is a six-tab PWA; this has
 * none for a different reason — the site is one page whose content is a
 * configured arrangement of sections, and the only *other* view is the guest's
 * own visits. A hash carries that, which means the back button works, "your
 * visits" is linkable, and no route table has to agree with a section registry.
 * A URL that survives a paste is a tenant's own public page either way.
 */

type View = 'site' | 'visits';

export function App({ api }: { api: GuestApi }): ReactNode {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'ready'; site: SiteResponse } | { kind: 'failed'; error: unknown }
  >({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    api
      .site(controller.signal)
      .then((site) => {
        if (!controller.signal.aborted) setState({ kind: 'ready', site });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setState({ kind: 'failed', error });
      });
    return () => {
      controller.abort();
    };
  }, [api]);

  const theme = useMemo<ResolvedTheme>(
    () => resolveTheme(state.kind === 'ready' ? state.site.site.branding : {}),
    [state],
  );

  useEffect(() => {
    applyTheme(document.documentElement, theme);
    if (state.kind === 'ready') document.title = state.site.site.name;
  }, [theme, state]);

  if (state.kind === 'loading') return <Busy label="Loading…" />;
  if (state.kind === 'failed') return <Unresolved error={asFailure(state.error)} />;

  return (
    <SiteProvider api={api} site={state.site} theme={theme}>
      <Page />
    </SiteProvider>
  );
}

/**
 * What a browser sees when `/api/site` did not answer with a site.
 *
 * `404` is the important one and it is not an error: `guest-api` answers it for
 * a hostname no `tenant_domains` row claims **and** for a tenant whose
 * `booking_site` module is off — deliberately the same answer, because "this
 * site exists but is switched off" is a state §3.2 refuses to leak. So the page
 * says there is nothing here and stops. It names no tenant, offers no list, and
 * does not suggest trying another address: this bundle is served on hundreds of
 * hostnames and must never hint that the others exist.
 */
function Unresolved({ error }: { error: ApiFailure }): ReactNode {
  if (error.code === 'not_found') {
    return (
      <main className="shell shell--bare">
        <Notice tone="warn" title="There is no site at this address." />
      </main>
    );
  }

  return (
    <main className="shell shell--bare">
      <Notice tone="error" title={error.message}>
        <p className="notice__hint">Please try again in a moment.</p>
      </Notice>
    </main>
  );
}

function Page(): ReactNode {
  const { site, signedIn, guest, signOut } = useSite();
  const view = useHashView();

  const arrangement = useMemo(() => arrangeSections(site.branding), [site.branding]);
  const hasBookingBlock = arrangement.sections.some((s) => s.type === 'booking');

  return (
    <div className="shell">
      <nav className="shell__nav" aria-label="This site">
        <a className="shell__home" href="#/">
          {site.name}
        </a>
        <div className="shell__navright">
          <a className="shell__link" href="#/visits">
            Your visits
          </a>
          {signedIn ? (
            <button className="button button--quiet" type="button" onClick={() => void signOut()}>
              {guest === null ? 'Sign out' : `Sign out (${guest.displayName})`}
            </button>
          ) : null}
        </div>
      </nav>

      <main className="shell__body">
        {view === 'visits' ? (
          <Visits />
        ) : (
          <>
            {arrangement.sections.map((section) => (
              <SectionView
                key={section.key}
                section={section}
                ctx={{ bookingFlow: <BookingFlow /> }}
              />
            ))}
            {/* A tenant whose arrangement omits `booking` still books. A booking
                site a tenant can accidentally configure into being unable to
                take a booking would be a footgun dressed as flexibility. */}
            {hasBookingBlock ? null : (
              <section className="section booking" id="book">
                <h2 className="section__title">Book a time</h2>
                <BookingFlow />
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/**
 * The whole router: `#/visits` or everything else.
 *
 * `hashchange` rather than `popstate` and `history.pushState`, because the SPA
 * lives in a Cloud Storage bucket behind the LB and a real path would need the
 * URL map to rewrite every unknown path to `index.html`. A hash never reaches
 * the server, so deep links work with no bucket configuration and no per-tenant
 * anything — which is the same instinct as everything else in §6.4.2.
 */
function useHashView(): View {
  const read = useCallback(
    (): View => (globalThis.location.hash.startsWith('#/visits') ? 'visits' : 'site'),
    [],
  );
  const [view, setView] = useState<View>(read);

  useEffect(() => {
    const onChange = (): void => {
      setView(read());
    };
    globalThis.addEventListener('hashchange', onChange);
    return () => {
      globalThis.removeEventListener('hashchange', onChange);
    };
  }, [read]);

  return view;
}
